import {
  FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { FC, useEffect, useState } from "react";
import { useController, Control } from "react-hook-form";
import LabelComponent from "../../atom/Label/LabelComponent";
import { Option } from "../../../types/General/Option";
import { REQUIRED_FIELD } from "../../../utils/constants";
import SpinnerComponent from "../../atom/Spinner/SpinnerComponent";

interface RadioControlGroupProps {
  errors: any;
  name: string;
  control: Control<any>;
  data?: Option[];
  fieldName: string;
  fetchOptions?: () => Promise<Option[]>;
  isRequired?: boolean;
  isDisabled?: boolean;
}

const RadioControlGroup: FC<RadioControlGroupProps> = ({
  errors,
  name,
  control,
  data,
  fieldName,
  fetchOptions,
  isRequired = false,
  isDisabled = false,
}) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);

  const {
    field: { onChange, value },
  } = useController({
    name,
    control,
    defaultValue: null,
  });

  useEffect(() => {
    if (fetchOptions) {
      setIsLoaded(false);
      fetchOptions()
        .then((result) => {
          setOptions(result);
          setIsLoaded(true);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setIsLoaded(true);
        });
    } else if (data) {
      setOptions(data);
      setIsLoaded(true);
    } else {
      setOptions([]);
      setIsLoaded(true);
    }
  }, [fetchOptions, data]);

  const handleChange = (selectedId: string) => {
    const selectedOption = options.find(
      (option) => option.id.toString() === selectedId,
    );
    onChange(selectedOption || null);
  };

  return (
    <FormControl isInvalid={errors && !!errors[name]}>
      <LabelComponent
        fontSize="md"
        fontWeight="500"
        title={fieldName}
        isRequired={isRequired}
      />
      {isLoaded ? (
        <RadioGroup onChange={handleChange} value={value?.id?.toString() ?? ""}>
          <Stack direction="column">
            {options.map((option: Option) => (
              <Radio
                key={option.id}
                value={option.id.toString()}
                isDisabled={isDisabled}
              >
                {option.name}
              </Radio>
            ))}
          </Stack>
        </RadioGroup>
      ) : (
        <SpinnerComponent size="lg" />
      )}
      <FormErrorMessage>
        {errors && errors[name] && <>{REQUIRED_FIELD}</>}
      </FormErrorMessage>
    </FormControl>
  );
};

export default RadioControlGroup;
